import "@css/temp_view2.css";
import type * as type from "@vTypes";
import type { ShiftManage } from "@vTypes/snapshot-type";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import LoadingDots from "@components/LoadingDots";
import { toast } from "@/components/toast/CustomToast";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth";
import { RosterTabs } from "@components/feature/roster_view/components/RosterTabs.tsx";
import useIssuedList from "@hooks/useIssuedList";
import useSnapShot from "@hooks/useSnapShot.ts";
import { useHolidayDatesQuery } from "@/hooks/useHolidayDatesQuery";
import {
  generatePreceptorGroupIDs,
  insertPreceptorData,
} from "@utils/preceptorUtils";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { pre_monthlyNurseSchedule } from "@/utils/rosterCreate-util";
import type { RosterViewOutletContext } from "@/pages/roster-view/Roster_view_outlet_routes";
import type { IssuedAssignmentsViewBundle } from "@vTypes/snapshot-type";

function Roster_view() {
  const loginUser = useLoginUser();
  const { selectedGroupId } = useGroupSelection();
  const isAdmin = useIsAdmin();
  const gid = isAdmin ? (selectedGroupId ?? "") : "";

  const { data: issuedList = [], isLoading: issuedLoading } =
    useIssuedList(gid);

  type ScheduleDate = {
    year: number;
    month: number;
    schedule_id: string;
  };

  const [currentScheduleDate, setCurrentScheduleDate] = useState<
    ScheduleDate | undefined
  >();

  const scheduleDateRef = useRef<ScheduleDate | undefined>(undefined);
  scheduleDateRef.current = currentScheduleDate;

  const location = useLocation();

  /**
   * 발행 목록 refetch만 일어나면 재실행해도 드롭다운 선택을 덮어쓰지 않도록,
   * `그룹 + URL search` 조합이 바뀐 적이 있을 때만 URL → state 동기화.
   * (관리자 gid 지연 로드·알람 딥링크가 최신 목록만 보는 현상 방지)
   */
  const appliedGroupAndSearchKey = useRef<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  /** URL `group_id` 없으면 로그인 소속 — `GET /roster/issued_roster?group_id=` 와 동일 */
  const issuedSnapshotGroupId = useMemo(() => {
    const url = searchParams.get("group_id")?.trim() ?? "";
    const home = (loginUser?.group_id ?? "").trim();
    if (url !== "") return url;
    return home;
  }, [searchParams, loginUser?.group_id]);

  const setIssuedSnapshotGroupId = useCallback(
    (rawGroupId: string) => {
      const sp = new URLSearchParams(location.search);
      if (currentScheduleDate) {
        sp.set("year", String(currentScheduleDate.year));
        sp.set("month", String(currentScheduleDate.month));
      }
      const home = (loginUser?.group_id ?? "").trim();
      const next = rawGroupId.trim();
      if (next === "" || next === home) {
        sp.delete("group_id");
      } else {
        sp.set("group_id", next);
      }
      setSearchParams(sp, { replace: true });
    },
    [location.search, currentScheduleDate, loginUser?.group_id, setSearchParams]
  );

  const setCurrentScheduleDateSynced = useCallback(
    (action: React.SetStateAction<ScheduleDate | undefined>) => {
      const prev = scheduleDateRef.current;
      const next = typeof action === "function" ? action(prev) : action;
      setCurrentScheduleDate(action);
      if (next) {
        const sp = new URLSearchParams(location.search);
        sp.set("year", String(next.year));
        sp.set("month", String(next.month));
        appliedGroupAndSearchKey.current = `${gid}|?${sp.toString()}`;
        setSearchParams(sp, { replace: true });
      }
    },
    [gid, location.search, setSearchParams]
  );

  const {
    data: rawSnapShotData,
    isPending,
    isFetching,
  } = useSnapShot({
    year: currentScheduleDate?.year,
    month: currentScheduleDate?.month,
    groupId: issuedSnapshotGroupId,
  });

  console.log("rawSnapShotData:", rawSnapShotData);

  const snapShotData = useMemo(() => {
    return rawSnapShotData;
  }, [rawSnapShotData]);

  const fullNurseSchema = snapShotData?.nurses;

  const snapshotAligned = useMemo(() => {
    if (
      snapShotData == null ||
      currentScheduleDate == null ||
      snapShotData.meta == null
    )
      return false;
    return (
      Number(snapShotData.meta.year) === Number(currentScheduleDate.year) &&
      Number(snapShotData.meta.month) === Number(currentScheduleDate.month)
    );
  }, [snapShotData, currentScheduleDate]);

  const isRosterSnapshotLoading =
    currentScheduleDate != null &&
    loginUser != null &&
    (issuedList?.length ?? 0) > 0 &&
    !snapshotAligned &&
    (isPending || isFetching);

  const preceptorGroupID = useMemo(
    () => generatePreceptorGroupIDs(fullNurseSchema ?? []),
    [fullNurseSchema]
  );

  useLayoutEffect(() => {
    if (issuedLoading) return;
    if (!issuedList?.length) return;

    const syncKey = `${gid}|${location.search}`;
    if (appliedGroupAndSearchKey.current === syncKey) return;

    const params = new URLSearchParams(location.search);
    const qy = params.get("year");
    const qm = params.get("month");

    if (qy && qm) {
      const y = parseInt(qy, 10);
      const m = parseInt(qm, 10);
      if (!Number.isNaN(y) && !Number.isNaN(m)) {
        const matchedSchedule = issuedList.find(
          (item) => Number(item.year) === y && Number(item.month) === m
        );
        if (matchedSchedule) {
          setCurrentScheduleDate(matchedSchedule);
          appliedGroupAndSearchKey.current = syncKey;
          return;
        }
        toast.error(`${y}년 ${m}월 근무표를 찾을 수 없습니다.`, {
          description:
            "마감이 철회된 근무표이거나 잘못된 경로일 수 있습니다. 가장 최근 발행 월을 표시합니다.",
        });
        setCurrentScheduleDate(issuedList[0]);
        appliedGroupAndSearchKey.current = syncKey;
        return;
      }
    }

    setCurrentScheduleDate(issuedList[0]);
    appliedGroupAndSearchKey.current = syncKey;
  }, [issuedList, location.search, issuedLoading, gid]);

  const { data: holidayDates = [] } = useHolidayDatesQuery(
    currentScheduleDate?.year,
    currentScheduleDate?.month
  );

  /** 발행 조회: 시프트 카탈로그는 스냅샷 `shifts`만 사용(발행 시점 정의). 로딩·미정렬 구간은 빈 배열 */
  const viewCatalogShifts = useMemo((): type.availableShifts[] => {
    if (!snapshotAligned || !snapShotData?.shifts?.length) return [];
    const byShiftId = new Map<string, (typeof snapShotData.shifts)[number]>();
    for (const s of snapShotData.shifts) {
      const prev = byShiftId.get(s.shift_id);
      if (!prev || s.id > prev.id) byShiftId.set(s.shift_id, s);
    }
    return [...byShiftId.values()] as unknown as type.availableShifts[];
  }, [snapshotAligned, snapShotData]);

  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    viewCatalogShifts,
    null
  );

  const fullNurseSchemaAddPreceptor = useMemo(() => {
    if (!fullNurseSchema) return undefined;
    return insertPreceptorData(fullNurseSchema, preceptorGroupID);
  }, [fullNurseSchema, preceptorGroupID]);

  const outletContext = useMemo((): RosterViewOutletContext | null => {
    if (!currentScheduleDate || !issuedList?.length) return null;

    const rosterSheetResolved =
      snapshotAligned && snapShotData?.roster ? snapShotData.roster : null;

    const monthlyScheduleResolved =
      snapshotAligned &&
      rosterSheetResolved &&
      fullNurseSchemaAddPreceptor != null
        ? pre_monthlyNurseSchedule(
            fullNurseSchemaAddPreceptor,
            rosterSheetResolved.nurses
          )
        : null;

    const shiftManager: ShiftManage | ShiftManage[] = snapshotAligned
      ? snapShotData!.shift_manage
      : ([] as ShiftManage[]);

    const issuedAssignmentsView: IssuedAssignmentsViewBundle | null =
      snapshotAligned
        ? {
            assignments: null,
            groups: snapShotData!.groups ?? null,
            viewer:
              loginUser != null &&
              loginUser.nurse_id != null &&
              loginUser.nurse_id !== ""
                ? {
                    nurse_id: String(loginUser.nurse_id),
                    group_id: loginUser.group_id ?? "",
                    group_name:
                      (loginUser.mb_part_name ?? "").trim() ||
                      (loginUser.office_name ?? "").trim() ||
                      (loginUser.group_id ?? "").trim() ||
                      "",
                    office_id: loginUser.office_id ?? "",
                  }
                : null,
          }
        : null;

    return {
      loginUser,
      issuedList,
      currentScheduleDate,
      setCurrentScheduleDate: setCurrentScheduleDateSynced,
      holidayDates,
      selectedGroupId: selectedGroupId ?? null,
      availableShifts: viewCatalogShifts,
      shiftLookupMaps,
      rosterSheet: rosterSheetResolved,
      monthlySchedule: monthlyScheduleResolved,
      shiftManager,
      issuedDate: snapshotAligned ? snapShotData!.created_at : undefined,
      scheduleId: snapshotAligned ? snapShotData!.schedule_id : undefined,
      issuedMemo: snapshotAligned ? (snapShotData!.meta?.memo ?? "") : "",
      isRosterSnapshotLoading,
      issuedAssignmentsView,
      issuedSnapshotGroupId,
      setIssuedSnapshotGroupId,
    };
  }, [
    snapshotAligned,
    snapShotData,
    currentScheduleDate,
    issuedList,
    fullNurseSchemaAddPreceptor,
    loginUser,
    holidayDates,
    selectedGroupId,
    viewCatalogShifts,
    shiftLookupMaps,
    isRosterSnapshotLoading,
    setCurrentScheduleDateSynced,
    issuedSnapshotGroupId,
    setIssuedSnapshotGroupId,
  ]);

  if (issuedLoading) {
    return (
      <div className="rosterCreating">
        발행 목록을 불러오는 중입니다.
        <LoadingDots />
      </div>
    );
  }

  if (!issuedList?.length)
    return (
      <div className="w_main">
        <div
          style={{ color: "#6d6d86" }}
          className="flex flex-col items-center h-full justify-center gap-3.5 "
        >
          <h1 className="text-3xl! font-bold!">
            현재 발행된 근무표가 없습니다.
          </h1>
          <p className="text-lg">근무표 발행은 담당자에게 문의해주세요.</p>
        </div>
      </div>
    );

  if (!currentScheduleDate || !outletContext) {
    return (
      <div className="font-pretendard w-full">
        <div className="px-5 py-10">
          <RosterTabs />
        </div>
        <div className="flex justify-center py-16 text-zinc-500">
          <LoadingDots />
        </div>
      </div>
    );
  }

  return (
    <div className="font-pretendard w-full">
      <div className="px-5 py-10">
        <RosterTabs />
      </div>
      <Outlet context={outletContext} />
    </div>
  );
}

export default Roster_view;
